import { useEffect, useMemo, useState } from 'preact/hooks';
import { XP_REASON_LABELS, isPvpMode, type MapVote, type PlayerOutcome } from '@cratered/shared';
import { sfx } from '../game/audio';
import { mapBlurb, mapName } from '../game/maps';
import { TEAM_COLORS } from '../game/render';
import { isHost, lobby, match, me, net, results, screen, self } from '../store';
import { Avatar, MoleCanvas, TeamDot } from './common';

const BONUS = new Set(['flawless', 'domination', 'quick', 'comeback', 'dailyFirstWin', 'stars']);

export function Results() {
  const r = results.value;
  const m = match.value;
  const myId = self.value?.id ?? '';
  const [fill, setFill] = useState(0);
  const mine = r?.rewards[myId];

  // The result sting, once, when the screen comes up.
  useEffect(() => {
    const o = r?.outcomes.find((x) => x.playerId === myId);
    if (o) sfx.fanfare(o.result === 'win' ? 'win' : o.result === 'loss' ? 'lose' : 'draw');
  }, [r]);

  useEffect(() => {
    if (!mine) return;
    setFill(mine.levelBefore.progress);
    const t = setTimeout(() => setFill(mine.levelAfter.progress), 300);
    return () => clearTimeout(t);
  }, [mine?.xpGained]);

  if (!r || !m) return null;
  const ranked = [...r.outcomes].sort(rank);
  const nameFor = (o: PlayerOutcome) => m.players.find((p) => p.id === o.playerId);
  const host = isHost.value;
  const hostName = lobby.value?.players.find((p) => p.id === lobby.value?.hostId)?.name ?? 'the host';
  // Versus and brawl are team games, so they get ranked standings with team dots; PvE is a shared win or loss.
  const pvp = isPvpMode(m.settings.mode);
  const vote = lobby.value?.vote;

  return (
    <div class="screen">
      <div class="results">
        <section class="panel">
          <div class="panel-head">
            <h2>{pvp ? 'Final standings' : ranked[0]?.result === 'win' ? 'Victory' : 'Defeat'}</h2>
            <span class="muted num">{ranked[0]?.roundsPlayed ?? 0} rounds</span>
          </div>
          <div class="panel-body">
            {vote && <VotePanel vote={vote} />}
            {ranked.map((o, i) => {
              const p = nameFor(o);
              const rw = r.rewards[o.playerId];
              return (
                <div key={o.playerId} class={`rank-row${i === 0 && o.result === 'win' ? ' first' : ''}`}>
                  <span class="rank num">{pvp ? i + 1 : o.result === 'win' ? '★' : '—'}</span>
                  <Avatar name={p?.name ?? '?'} url={p?.avatarUrl} large />
                  <div style={{ minWidth: 0 }}>
                    <div class="name" style={{ fontWeight: 800, display: 'flex', gap: 8, alignItems: 'center' }}>
                      {pvp && <TeamDot team={p?.team ?? 0} />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.name ?? o.playerId}</span>
                      {o.playerId === myId && <span class="muted">(you)</span>}
                    </div>
                    <div class="muted num" style={{ fontSize: 12 }}>
                      {o.result === 'win' ? 'Won' : o.result === 'draw' ? 'Draw' : 'Lost'} · {o.unitsAlive}/{o.unitsStart} moles · {o.hpRemaining} HP left
                      {rw && rw.levelAfter.level > rw.levelBefore.level ? ` · Level up → ${rw.levelAfter.level}` : ''}
                    </div>
                  </div>
                  <span class="num" style={{ fontWeight: 800, color: 'var(--accent)' }}>
                    {rw ? `+${rw.xpGained} XP` : ''}
                  </span>
                </div>
              );
            })}
            <div class="btn-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button class="btn ghost" onClick={() => (screen.value = 'locker')}>
                Locker
              </button>
              {vote ? (
                <span class="muted vote-status num">
                  Starting the voted map in <Countdown vote={vote} />s
                </span>
              ) : host ? (
                <>
                  <button class="btn" onClick={() => net.value?.send({ t: 'backToLobby' })}>
                    Back to lobby
                  </button>
                  <button
                    class="btn primary big"
                    onClick={() => {
                      net.value?.send({ t: 'backToLobby' });
                      setTimeout(() => net.value?.send({ t: 'startMatch' }), 150);
                    }}
                  >
                    Play again
                  </button>
                </>
              ) : (
                <span class="muted">Waiting for {hostName}…</span>
              )}
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <h2>Your XP</h2>
            {mine && <span class="muted num">Lv {mine.levelBefore.level}{mine.levelAfter.level !== mine.levelBefore.level ? ` → ${mine.levelAfter.level}` : ''}</span>}
          </div>
          <div class="panel-body">
            {mine ? (
              <>
                <div class="xp-bar" aria-hidden="true">
                  <span style={{ width: `${fill * 100}%` }} />
                </div>
                <div>
                  {mine.breakdown.lines.map((l) => (
                    <div key={l.reason} class={`xp-line${BONUS.has(l.reason) ? ' bonus' : ''}`}>
                      <span>{XP_REASON_LABELS[l.reason]}</span>
                      <span class="count num">{l.count > 1 || l.reason === 'hit' || l.reason === 'kill' ? `×${l.count}` : ''}</span>
                      <span class="amount num">+{l.amount}</span>
                    </div>
                  ))}
                  <div class="xp-total">
                    <span>Total</span>
                    <span class="big num">+{mine.xpGained}</span>
                  </div>
                </div>
                {mine.newUnlocks.length > 0 && (
                  <>
                    <p class="eyebrow">New unlocks</p>
                    {mine.newUnlocks.map((u) => (
                      <div key={u.id} class="new-unlock">
                        <div class="card-art">
                          {u.kind === 'body' || u.kind === 'outfit' || u.kind === 'race' ? (
                            <MoleCanvas look={{ race: u.kind === 'race' ? u.id : undefined, body: u.kind === 'body' ? u.id : 'body.lean', outfit: u.kind === 'outfit' ? u.id : 'outfit.none', teamColor: TEAM_COLORS[0] }} />
                          ) : (
                            <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--warn)' }}>★</span>
                          )}
                        </div>
                        <div>
                          <div class="card-name">{u.name}</div>
                          <div class="card-desc">{u.kind === 'perk' ? 'Perk · Co-op & Campaign' : u.description}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <p class="muted">You were spectating. Join the next one!</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * The next-map vote: a card per option with its tally and voters, clickable by anyone who will be
 * in the match (spectators only watch). The server starts the winner when the clock runs out.
 */
function VotePanel({ vote }: { vote: MapVote }) {
  const players = lobby.value?.players ?? [];
  const myId = self.value?.id ?? '';
  const canVote = !!me.value && !me.value.spectator;
  const mine = vote.votes[myId];
  return (
    <div class="vote">
      <div class="vote-head">
        <span class="eyebrow">Vote for the next map</span>
        <span class="muted num">
          <Countdown vote={vote} />s
        </span>
      </div>
      <div class="vote-cards">
        {vote.options.map((id) => {
          const voters = players.filter((p) => vote.votes[p.id] === id);
          return (
            <button
              key={id}
              type="button"
              class={`vote-card${mine === id ? ' chosen' : ''}`}
              disabled={!canVote}
              aria-pressed={mine === id}
              onClick={() => net.value?.send({ t: 'vote', map: id })}
            >
              <span class="vote-count num">{voters.length}</span>
              <span class="vote-name">{mapName(id)}</span>
              <span class="vote-blurb">{mapBlurb(id)}</span>
              <span class="vote-voters avatar-stack">
                {(voters.length > 4 ? voters.slice(0, 3) : voters).map((p) => (
                  <Avatar key={p.id} name={p.name} url={p.avatarUrl} />
                ))}
                {voters.length > 4 && <span class="more num">+{voters.length - 3}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Whole seconds left in the vote, ticking down. The server says how long was left when it sent the
 * vote; counting that on our own clock keeps the number right whatever our clock reads.
 */
function Countdown({ vote }: { vote: MapVote }) {
  const end = useMemo(() => (vote.receivedAt ?? Date.now()) + (vote.leftMs ?? vote.endsAt - Date.now()), [vote]);
  const left = () => Math.max(0, Math.ceil((end - Date.now()) / 1000));
  const [n, setN] = useState(left);
  useEffect(() => {
    setN(left());
    const t = setInterval(() => setN(left()), 250);
    return () => clearInterval(t);
  }, [end]);
  return <>{n}</>;
}

function rank(a: PlayerOutcome, b: PlayerOutcome): number {
  const score = (o: PlayerOutcome) => (o.result === 'win' ? 2 : o.result === 'draw' ? 1 : 0);
  return score(b) - score(a) || b.hpRemaining - a.hpRemaining || b.unitsAlive - a.unitsAlive;
}
